import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Link,
  LinearProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import PageLayout from '../../components/layout/PageLayout';
import {
  getLeadById,
  uploadLeadDocumentFile,
  updateLeadDocumentFile,
} from '../../store/slices/leadsSlice';
import { getErrorMessage } from '../../utils/errorHandler';
import { LEADS_PATH } from '../../navigations/path';
import { containedPrimaryBtnSx, outlinedPrimaryBtnSx } from '../../theme/themedSx';
import { alpha } from '@mui/material/styles';

/** Steps aligned with lead listing attachment checklist */
export const LEAD_DOCUMENT_STEPS = [
  {
    key: 'ein',
    shortLabel: 'EIN document',
    title: 'Upload EIN document',
    description: 'Upload your Employer Identification Number (EIN) letter or confirmation.',
    optional: false,
  },
  {
    key: 'ssn',
    shortLabel: 'SSN document',
    title: 'Upload SSN document',
    description: 'Upload Social Security Number document or card (front).',
    optional: false,
  },
  {
    key: 'driving_license',
    shortLabel: 'Driving license',
    title: 'Upload Driving license',
    description: 'Upload a clear photo or scan of the driving license.',
    optional: false,
  },
  {
    key: 'voided_cheque',
    shortLabel: 'Voided cheque / Bank opening letter / E-Cheque',
    title: 'Upload Voided cheque / Bank opening letter / E-Cheque',
    description: 'Upload a voided cheque, bank opening letter, or e-cheque.',
    optional: false,
  },
  {
    key: 'additional',
    shortLabel: 'Additional (optional)',
    title: 'Upload Additional document',
    description: 'Upload any additional supporting document (optional).',
    optional: true,
  },
];

const ALLOWED_MIME = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_BYTES = 50 * 1024 * 1024;

function normalizeDocKey(title) {
  return String(title || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '_');
}

function validateWizardFile(file) {
  const errors = [];
  const type = file?.type || '';
  const size = file?.size || 0;
  if (!ALLOWED_MIME.includes(type)) {
    errors.push('Only PDF, JPG, and PNG are allowed');
  }
  if (size > MAX_BYTES) {
    errors.push('File must be 50MB or smaller');
  }
  return { isValid: errors.length === 0, errors };
}

function extractAttachmentId(response) {
  return (
    response?.data?.id ||
    response?.id ||
    response?.data?.attachment?.id ||
    response?.attachment?.id ||
    response?.attachment_id ||
    response?.data?.data?.id ||
    null
  );
}

const LeadUploadDocuments = () => {
  const { id: leadId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [lead, setLead] = useState(null);
  const [loadingLead, setLoadingLead] = useState(true);
  const [activeStep, setActiveStep] = useState(0);
  const [stepState, setStepState] = useState(() =>
    Object.fromEntries(
      LEAD_DOCUMENT_STEPS.map((s) => [s.key, { status: 'pending', fileName: '', attachmentId: null }])
    )
  );
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragOver, setDragOver] = useState(false);

  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const currentStep = LEAD_DOCUMENT_STEPS[activeStep];
  const currentMeta = stepState[currentStep?.key] || { status: 'pending' };
  const canProceed =
    currentMeta.status === 'uploaded' ||
    currentMeta.status === 'skipped' ||
    currentStep?.optional;

  const hydrateFromLead = useCallback((leadData) => {
    const uploaded = Array.isArray(leadData?.attachments) ? leadData.attachments : [];
    setStepState((prev) => {
      const next = { ...prev };
      LEAD_DOCUMENT_STEPS.forEach((step) => {
        const match = uploaded.find((a) => normalizeDocKey(a?.title) === step.key);
        if (match) {
          next[step.key] = {
            status: 'uploaded',
            fileName: match.file_name || match.title || match.name || 'Uploaded file',
            attachmentId: match.id || match.attachment_id || null,
          };
        }
      });
      return next;
    });

    const firstPending = LEAD_DOCUMENT_STEPS.findIndex((step) => {
      const match = uploaded.find((a) => normalizeDocKey(a?.title) === step.key);
      return !match;
    });
    if (firstPending >= 0) setActiveStep(firstPending);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!leadId) return;
      try {
        setLoadingLead(true);
        const res = await dispatch(getLeadById(leadId)).unwrap();
        const leadData = res?.data ?? res;
        if (cancelled) return;
        setLead(leadData);
        hydrateFromLead(leadData);
      } catch (err) {
        toast.error(err?.message || 'Failed to load lead');
        navigate(LEADS_PATH);
      } finally {
        if (!cancelled) setLoadingLead(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dispatch, leadId, hydrateFromLead, navigate]);

  const uploadFile = useCallback(
    async (file) => {
      if (!file || !leadId || uploading) return;

      const validation = validateWizardFile(file);
      if (!validation.isValid) {
        toast.error(validation.errors.join(', '));
        return;
      }

      const stepKey = LEAD_DOCUMENT_STEPS[activeStep]?.key;
      if (!stepKey) return;

      const existingAttachmentId = stepState[stepKey]?.attachmentId;

      setUploading(true);
      setUploadProgress(0);

      try {
        const res = await dispatch(
          existingAttachmentId
            ? updateLeadDocumentFile({
                attachmentId: existingAttachmentId,
                leadId,
                file,
                file_title: stepKey,
                onUploadProgress: setUploadProgress,
              })
            : uploadLeadDocumentFile({
                leadId,
                file,
                file_title: stepKey,
                onUploadProgress: setUploadProgress,
              })
        ).unwrap();

        const attachmentId = extractAttachmentId(res) || existingAttachmentId;
        setStepState((prev) => ({
          ...prev,
          [stepKey]: {
            status: 'uploaded',
            fileName: file.name,
            attachmentId,
          },
        }));
        setUploadProgress(100);
        toast.success(
          existingAttachmentId
            ? 'Document updated successfully.'
            : 'Document uploaded successfully.'
        );
      } catch (err) {
        toast.error(getErrorMessage(err) || err || 'Failed to upload attachment');
        setUploadProgress(0);
      } finally {
        setUploading(false);
      }
    },
    [activeStep, dispatch, leadId, stepState, uploading]
  );

  const goNext = () => {
    if (activeStep >= LEAD_DOCUMENT_STEPS.length - 1) {
      toast.success('All document steps completed');
      navigate(LEADS_PATH);
      return;
    }
    setActiveStep((s) => s + 1);
    setUploadProgress(0);
  };

  const goToStep = (index) => {
    if (uploading) return;
    if (index < 0 || index >= LEAD_DOCUMENT_STEPS.length) return;
    if (index === activeStep) return;
    setActiveStep(index);
    setUploadProgress(0);
  };

  const handleBackStep = () => {
    if (uploading || activeStep <= 0) return;
    setActiveStep((s) => s - 1);
    setUploadProgress(0);
  };

  const handleSkip = () => {
    if (uploading) return;
    const key = currentStep.key;
    setStepState((prev) => ({
      ...prev,
      [key]: {
        ...prev[key],
        status: prev[key]?.status === 'uploaded' ? 'uploaded' : 'skipped',
      },
    }));
    goNext();
  };

  const handleContinue = () => {
    if (uploading) return;
    if (!canProceed) {
      toast.warn('Please upload a document or skip this step');
      return;
    }
    goNext();
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    if (uploading) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) uploadFile(file);
  };

  const stepperItems = useMemo(
    () =>
      LEAD_DOCUMENT_STEPS.map((step, index) => {
        const meta = stepState[step.key];
        const isActive = index === activeStep;
        const isSkipped = meta?.status === 'skipped';
        const isDone = meta?.status === 'uploaded';
        return { step, index, isActive, isSkipped, isDone, meta };
      }),
    [activeStep, stepState]
  );

  if (loadingLead) {
    return (
      <PageLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ mb: 3 }}>
          <Breadcrumbs sx={{ mb: 2 }}>
            <Link
              color="inherit"
              onClick={() => navigate(LEADS_PATH)}
              sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
            >
              Leads
            </Link>
            <Typography color="text.primary">Upload Documents</Typography>
          </Breadcrumbs>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 600, mb: 1 }}>
                Upload Documents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Lead:{' '}
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {lead?.name || lead?.dba_name || '—'}
                </Box>
                {lead?.dba_name ? ` · ${lead.dba_name}` : ''}
              </Typography>
            </Box>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate(LEADS_PATH)}
              variant="outlined"
              color="primary"
              sx={outlinedPrimaryBtnSx}
            >
              Back to Leads
            </Button>
          </Box>
        </Box>

        <Card sx={{ mb: 3 }}>
          <CardContent sx={{ pt: 3, pb: 2 }}>
            <Stepper
              activeStep={activeStep}
              alternativeLabel
              nonLinear
              sx={{
                overflowX: 'auto',
                pb: 1,
                '& .MuiStepLabel-label': {
                  typography: 'caption',
                  mt: 1,
                },
                '& .MuiStepLabel-labelContainer': {
                  cursor: uploading ? 'default' : 'pointer',
                },
              }}
            >
              {stepperItems.map(({ step, index, isSkipped, isDone }) => (
                <Step key={step.key} completed={isDone && !isSkipped}>
                  <StepLabel
                    onClick={() => {
                      if (!uploading) goToStep(index);
                    }}
                    optional={
                      isSkipped ? (
                        <Typography
                          variant="caption"
                          color="warning.main"
                          sx={{ display: 'block', fontWeight: 600 }}
                        >
                          Skipped
                        </Typography>
                      ) : step.optional ? (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: 'block' }}
                        >
                          Optional
                        </Typography>
                      ) : undefined
                    }
                    sx={{
                      cursor: uploading ? 'default' : 'pointer',
                      ...(isSkipped && {
                        '& .MuiStepIcon-root': { color: 'warning.main' },
                        '& .MuiStepIcon-root.Mui-completed': { color: 'warning.main' },
                      }),
                    }}
                  >
                    {step.shortLabel}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </CardContent>
        </Card>

        <Card>
          <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
            <Typography
              variant="h6"
              sx={{
                mb: 0.5,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 600,
              }}
            >
              <CloudUploadIcon color="primary" />
              {currentStep.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {currentStep.description}
            </Typography>

            <Box
              onDragEnter={(e) => {
                e.preventDefault();
                if (!uploading) setDragOver(true);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                if (!uploading) setDragOver(true);
              }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              onClick={() => {
                if (!uploading) fileInputRef.current?.click();
              }}
              sx={{
                border: '2px dashed',
                borderColor: dragOver ? 'primary.main' : 'divider',
                bgcolor: (theme) =>
                  dragOver
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.03),
                borderRadius: 2,
                py: 5,
                px: 2,
                textAlign: 'center',
                cursor: uploading ? 'wait' : 'pointer',
                mb: 2,
                pointerEvents: uploading ? 'none' : 'auto',
                transition: 'border-color 0.15s ease, background-color 0.15s ease',
              }}
            >
              {uploading ? (
                <CircularProgress size={42} sx={{ mb: 1 }} />
              ) : (
                <CloudUploadIcon sx={{ fontSize: 42, color: 'primary.main', mb: 1 }} />
              )}
              <Typography fontWeight={600} color="text.primary">
                {uploading ? 'Uploading document…' : 'Drag & drop or click to browse'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, JPG, PNG — max 50MB
              </Typography>
              {uploading && (
                <Box sx={{ mt: 2, px: { xs: 1, sm: 6 } }}>
                  <LinearProgress
                    variant="determinate"
                    value={uploadProgress}
                    color="primary"
                    sx={{ borderRadius: 1, height: 6 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Uploading… {uploadProgress}%
                  </Typography>
                </Box>
              )}
            </Box>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,image/jpeg,image/png,image/jpg"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadFile(file);
                e.target.value = '';
              }}
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) uploadFile(file);
                e.target.value = '';
              }}
            />

            <Button
              variant="outlined"
              color="primary"
              startIcon={<PhotoCameraIcon />}
              onClick={() => cameraInputRef.current?.click()}
              disabled={uploading}
              sx={{ ...outlinedPrimaryBtnSx, mb: 2 }}
            >
              Take photo
            </Button>

            {currentMeta.status === 'uploaded' && currentMeta.fileName && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  bgcolor: 'action.hover',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1.5,
                  px: 1.5,
                  py: 1,
                  mb: 1,
                }}
              >
                <InsertDriveFileIcon color="action" fontSize="small" />
                <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                  {currentMeta.fileName}
                </Typography>
                <Chip
                  size="small"
                  color="success"
                  icon={<CheckCircleIcon />}
                  label="Uploaded"
                  variant="outlined"
                />
              </Box>
            )}

            {currentMeta.status === 'uploaded' && (
              <Typography variant="body2" color="success.main" sx={{ mb: 2, fontWeight: 500 }}>
                Document uploaded successfully. You can continue to the next step.
              </Typography>
            )}

            {currentMeta.status === 'skipped' && (
              <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                This step was skipped.
              </Typography>
            )}

            <Box
              sx={{
                display: 'flex',
                gap: 1.5,
                mt: 2,
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { sm: 'center' },
              }}
            >
              <Button
                variant="outlined"
                color="primary"
                startIcon={<ArrowBackIcon />}
                onClick={handleBackStep}
                disabled={uploading || activeStep <= 0}
                sx={outlinedPrimaryBtnSx}
              >
                Back
              </Button>

              <Box
                sx={{
                  display: 'flex',
                  gap: 1.5,
                  flexDirection: { xs: 'column', sm: 'row' },
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                <Button
                  variant="outlined"
                  onClick={handleSkip}
                  disabled={uploading}
                  sx={{ textTransform: 'none' }}
                >
                  Skip for now
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleContinue}
                  disabled={uploading || !canProceed}
                  sx={{
                    ...containedPrimaryBtnSx,
                    minWidth: { sm: 180 },
                    py: 1.1,
                  }}
                >
                  {uploading
                    ? 'Uploading…'
                    : activeStep >= LEAD_DOCUMENT_STEPS.length - 1
                      ? 'Finish'
                      : 'Continue'}
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </PageLayout>
  );
};

export default LeadUploadDocuments;
